
import {
  Vue, Component, Prop, ProvideReactive, Watch,
} from 'vue-property-decorator';
import CheckoutForm from '@/features/shipment/components/checkout/CheckoutForm.vue';
import PaymentInfo from '@/features/shipment/components/checkout/PaymentInfo.vue';
import {
  getPaymentInformation,
} from '@/features/shipment/services/shipment.services';
import { AvailableService, InProgress, PaymentInfoResponse } from '@/features/shipment/types';
import { scrollToInPage } from '@/features/common/helpers';

@Component({
  components: {
    CheckoutForm,
    PaymentInfo,
  },
})
export default class CheckoutCard extends Vue {
  @Prop({ required: false, default: false }) isolate!: boolean;

  @Prop({ required: true }) showDetails!: boolean;

  private isVerificationRequired = false;

  @ProvideReactive() existingCardInfo: PaymentInfoResponse | null = null;

  private isCardInputsVisible = false;

  get forceEditQuery(): string {
    return this.$route.query.edit as string;
  }

  get forceResetQuery(): string {
    return this.$route.query.reset as string;
  }

  get selectedService(): AvailableService {
    return this.$store.state.shipment.selectedService;
  }

  get inProgress(): InProgress {
    return this.$store.getters.getInProgress;
  }

  get useSecureship(): boolean {
    if (this.isolate) return false;

    if (!this.selectedService) {
      return true;
    }

    if (this.inProgress.billing.account) {
      return false;
    }

    return this.selectedService.useSecureship;
  }

  async mounted(): Promise<void> {
    this.getPaymentInfo();
  }

  @Watch('forceEditQuery', { immediate: true })
  private forceShowEditPage(newValue: string) {
    if (newValue && newValue === '1') {
      this.isCardInputsVisible = true;
    }
  }

  @Watch('forceResetQuery', { immediate: true })
  private forceResetForm(newValue: string) {
    if (newValue && newValue === '1') {
      this.resetPaymentPage();
      this.getPaymentInfoAndSetVerification();
      scrollToInPage();
    }
  }

  private async getPaymentInfo() {
    this.$store.commit('setLoading', true);

    const existingInfo = await this.getPaymentInfoAndSetVerification();

    if (!existingInfo) {
      this.$store.commit('setLoading', false);
      return;
    }

    if (existingInfo.verificationRequired
      && !!existingInfo.maxAttemptsReached
      && this.useSecureship
    ) {
      this.resetPaymentPage();
    } else if (existingInfo.partialCard) {
      this.existingCardInfo = { ...existingInfo };
    } else if (existingInfo.paymentMethod === 'CreditCard' && this.useSecureship) {
      this.isCardInputsVisible = true;
    } else if (existingInfo.paymentMethod === 'CreditCard' && this.isolate) {
      this.isCardInputsVisible = true;
    }

    this.$store.commit('setLoading', false);
  }

  private async getPaymentInfoAndSetVerification() {
    const existingInfo = await getPaymentInformation();
    if (existingInfo && this.useSecureship) {
      this.isVerificationRequired = existingInfo.verificationRequired || false;
    }
    return existingInfo;
  }

  private resetPaymentPage() {
    this.isCardInputsVisible = true;
    this.existingCardInfo = null;
  }
}
